import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP } from '../utils/xp';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadGamePool, pickGameWords } from '../data/gameWords';
import './Anagram.css';

const TEXT = {
    title: { en: 'Anagram', it: 'Anagramma', es: 'Anagrama', fr: 'Anagramme', pt: 'Anagrama' },
    loading: { en: 'Mixing up the letters...', it: 'Sto mescolando le lettere...', es: 'Mezclando las letras...', fr: 'Mélange des lettres...', pt: 'Embaralhando as letras...' },
    notEnough: { en: 'Not enough words yet', it: 'Non ci sono ancora abbastanza parole', es: 'Aún no hay suficientes palabras', fr: 'Pas encore assez de mots', pt: 'Ainda não há palavras suficientes' },
    notEnoughHint: {
        en: 'Save a few words with meanings to your Word Bank and this game will use them.',
        it: 'Salva qualche parola con il significato nel tuo Word Bank e il gioco le userà.',
        es: 'Guarda algunas palabras con su significado en tu Word Bank y el juego las usará.',
        fr: 'Enregistrez quelques mots avec leur sens dans votre Word Bank et le jeu les utilisera.',
        pt: 'Salve algumas palavras com o significado no seu Word Bank e o jogo vai usá-las.',
    },
    meaning: { en: 'Unscramble the word that means', it: 'Rimetti in ordine la parola che significa', es: 'Ordena la palabra que significa', fr: 'Remettez en ordre le mot qui signifie', pt: 'Desembaralhe a palavra que significa' },
    shuffle: { en: 'Shuffle', it: 'Mescola', es: 'Mezclar', fr: 'Mélanger', pt: 'Embaralhar' },
    undo: { en: 'Undo', it: 'Annulla', es: 'Deshacer', fr: 'Annuler', pt: 'Desfazer' },
    skip: { en: 'Skip', it: 'Salta', es: 'Saltar', fr: 'Passer', pt: 'Pular' },
    correct: { en: 'Correct! ✓', it: 'Esatto! ✓', es: '¡Correcto! ✓', fr: 'Correct ! ✓', pt: 'Correto! ✓' },
    wrong: { en: 'Not quite, try again', it: 'Non proprio, riprova', es: 'Casi, inténtalo de nuevo', fr: 'Pas tout à fait, réessayez', pt: 'Quase, tente de novo' },
    timeUp: { en: "⏱ Time's up! It was", it: '⏱ Tempo scaduto! Era', es: '⏱ ¡Se acabó el tiempo! Era', fr: '⏱ Temps écoulé ! C’était', pt: '⏱ O tempo acabou! Era' },
    skipped: { en: 'It was', it: 'Era', es: 'Era', fr: 'C’était', pt: 'Era' },
    done: { en: 'Anagram complete!', it: 'Anagramma completato!', es: '¡Anagrama completado!', fr: 'Anagramme terminé !', pt: 'Anagrama concluído!' },
    review: { en: 'Review these', it: 'Da ripassare', es: 'Para repasar', fr: 'À revoir', pt: 'Para revisar' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
};

const ROUNDS = 8;
const ROUND_TIME = 30;

const stripAccents = (w) => String(w || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Words that scramble well: a single word of 4 to 9 letters with a short meaning
const usable = (item) => {
    if (!item.word || !item.translation || /\s/.test(item.word.trim())) return false;
    const key = stripAccents(item.word.trim());
    if (!/^[a-z]+$/.test(key)) return false;
    return key.length >= 4 && key.length <= 9 && new Set(key).size >= 3 && item.translation.length <= 50;
};

function scramble(word) {
    const letters = word.split('');
    let result = letters;
    for (let attempt = 0; attempt < 20; attempt++) {
        result = [...letters].sort(() => Math.random() - 0.5);
        if (result.join('') !== word) break;
    }
    return result.map((ch, i) => ({ id: i, ch }));
}

function chooseRounds(pool) {
    const picked = pickGameWords(pool, ROUNDS + 4, usable);
    const seen = new Set();
    const rounds = [];
    picked.forEach(item => {
        const key = stripAccents(item.word.trim());
        if (seen.has(key)) return;
        seen.add(key);
        rounds.push({ word: item.word.trim(), key, translation: item.translation.trim() });
    });
    return rounds.slice(0, ROUNDS);
}

export default function Anagram() {
    const navigate = useNavigate();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();

    const [gamePool, setGamePool] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [index, setIndex] = useState(0);
    const [tiles, setTiles] = useState([]);
    const [picked, setPicked] = useState([]);
    const [timeLeft, setTimeLeft] = useState(ROUND_TIME);
    const [status, setStatus] = useState('play');
    const [missed, setMissed] = useState([]);
    const [finished, setFinished] = useState(false);
    const [notEnough, setNotEnough] = useState(false);
    const [loading, setLoading] = useState(true);
    const [earnedXp, setEarnedXp] = useState(0);
    const scoreRef = useRef(0);
    const [score, setScore] = useState(0);
    const timerRef = useRef(null);
    const latest = useRef({});

    const begin = (pool) => {
        const list = chooseRounds(pool);
        if (list.length < 4) {
            setNotEnough(true);
            setLoading(false);
            return;
        }
        scoreRef.current = 0;
        setScore(0);
        setMissed([]);
        setFinished(false);
        setNotEnough(false);
        setRounds(list);
        setIndex(0);
        setTiles(scramble(list[0].word));
        setPicked([]);
        setStatus('play');
        setTimeLeft(ROUND_TIME);
        setLoading(false);
    };

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
            const loaded = await loadGamePool(user ? user.id : null, targetLang, interfaceLanguage, level);
            setGamePool(loaded);
            const list = chooseRounds(loaded);
            if (list.length < 4) {
                setNotEnough(true);
                setLoading(false);
                return;
            }
            scoreRef.current = 0;
            setRounds(list);
            setTiles(scramble(list[0].word));
            setLoading(false);
        };
        init();
        return () => clearTimeout(timerRef.current);
    }, [targetLang, activeLevel, interfaceLanguage]);

    const current = rounds[index];

    const goNext = (fromIndex, list) => {
        const next = fromIndex + 1;
        if (next >= list.length) {
            const xp = Math.min(20, scoreRef.current * 3);
            setEarnedXp(xp);
            setFinished(true);
            if (xp > 0) awardXP(xp, 'anagram');
            return;
        }
        setIndex(next);
        setTiles(scramble(list[next].word));
        setPicked([]);
        setStatus('play');
        setTimeLeft(ROUND_TIME);
    };

    const endRound = (result, wasMissed) => {
        setStatus(result);
        if (wasMissed) setMissed(prev => [...prev, current]);
        const fromIndex = index;
        const list = rounds;
        timerRef.current = setTimeout(() => goNext(fromIndex, list), 1100);
    };

    const tapTile = (id) => {
        if (status !== 'play' || picked.includes(id)) return;
        const nextPicked = [...picked, id];
        setPicked(nextPicked);
        if (nextPicked.length === tiles.length) {
            const guess = nextPicked.map(pid => tiles.find(t => t.id === pid).ch).join('');
            if (stripAccents(guess) === current.key) {
                scoreRef.current += 1;
                setScore(scoreRef.current);
                endRound('right', false);
            } else {
                setStatus('wrong');
                timerRef.current = setTimeout(() => { setPicked([]); setStatus('play'); }, 700);
            }
        }
    };

    const removeLast = () => {
        if (status !== 'play') return;
        setPicked(prev => prev.slice(0, -1));
    };

    const shuffleTiles = () => {
        if (status !== 'play') return;
        setTiles(scramble(current.word));
        setPicked([]);
    };

    const skip = () => {
        if (status !== 'play') return;
        endRound('skipped', true);
    };

    // Countdown for the current word
    useEffect(() => {
        if (loading || finished || notEnough || status !== 'play' || !current) return undefined;
        if (timeLeft <= 0) {
            latest.current.timeout();
            return undefined;
        }
        const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [timeLeft, status, loading, finished, notEnough, current]);

    // Typing on a keyboard works too
    useEffect(() => {
        const onKey = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'Backspace') { latest.current.removeLast(); return; }
            if (e.key.length !== 1) return;
            latest.current.typeLetter(stripAccents(e.key));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    latest.current = {
        timeout: () => endRound('timeout', true),
        removeLast,
        typeLetter: (letter) => {
            if (status !== 'play') return;
            const tile = tiles.find(t => !picked.includes(t.id) && stripAccents(t.ch) === letter);
            if (tile) tapTile(tile.id);
        },
    };

    const backBar = (
        <div className="an-top-bar">
            <button className="an-back" onClick={() => navigate('/games')}>←</button>
            <div className="an-title">{tx('title')}</div>
            <div style={{ width: 32 }}></div>
        </div>
    );

    if (loading) {
        return (
            <div className="an-screen">
                {backBar}
                <div className="an-complete"><div className="an-complete-stat">{tx('loading')}</div></div>
            </div>
        );
    }

    if (notEnough) {
        return (
            <div className="an-screen">
                {backBar}
                <div className="an-complete">
                    <div className="an-complete-icon">🔀</div>
                    <div className="an-complete-title">{tx('notEnough')}</div>
                    <div className="an-complete-stat">{tx('notEnoughHint')}</div>
                    <button className="an-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    if (finished) {
        return (
            <div className="an-screen">
                {backBar}
                <div className="an-complete">
                    <div className="an-complete-icon">🔀</div>
                    <div className="an-complete-title">{tx('done')}</div>
                    <div className="an-complete-score">{score} / {rounds.length}</div>
                    <div className="an-complete-xp">+{earnedXp} XP</div>
                    {missed.length > 0 && (
                        <div className="an-review">
                            <div className="an-review-label">{tx('review')}</div>
                            {missed.map((w, i) => (
                                <div key={i} className="an-review-row">
                                    <span className="an-review-word">{w.word}</span>
                                    <span className="an-review-translation">{w.translation}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="an-play-again" onClick={() => begin(gamePool)}>{tx('again')}</button>
                    <button className="an-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    const answerSlots = current.word.split('').map((_, i) => {
        const tileId = picked[i];
        const tile = tileId === undefined ? null : tiles.find(t => t.id === tileId);
        return tile ? tile.ch : '';
    });

    let banner = '';
    if (status === 'right') banner = tx('correct');
    if (status === 'wrong') banner = tx('wrong');
    if (status === 'timeout') banner = `${tx('timeUp')} “${current.word}”`;
    if (status === 'skipped') banner = `${tx('skipped')} “${current.word}”`;

    return (
        <div className="an-screen">
            {backBar}

            <div className="an-top-info">
                <div className="an-progress">{index + 1} / {rounds.length}</div>
                <div className="an-timer-track">
                    <div className="an-timer-fill" style={{ width: `${(timeLeft / ROUND_TIME) * 100}%` }}></div>
                </div>
                <div className="an-time">{timeLeft}s</div>
            </div>

            <div className="an-clue">
                <div className="an-clue-label">{tx('meaning')}</div>
                <div className="an-clue-text">“{current.translation}”</div>
            </div>

            <div className={`an-answer ${status === 'right' ? 'right' : ''} ${status === 'wrong' ? 'wrong' : ''}`}>
                {answerSlots.map((ch, i) => (
                    <div key={i} className={`an-slot ${ch ? 'filled' : ''}`}>{ch.toUpperCase()}</div>
                ))}
            </div>

            <div className="an-banner">{banner}</div>

            <div className="an-tiles">
                {tiles.map(t => (
                    <button
                        key={t.id}
                        className={`an-tile ${picked.includes(t.id) ? 'used' : ''}`}
                        onClick={() => tapTile(t.id)}
                        disabled={picked.includes(t.id) || status !== 'play'}
                    >
                        {t.ch.toUpperCase()}
                    </button>
                ))}
            </div>

            <div className="an-controls">
                <button className="an-ctrl-btn" onClick={shuffleTiles}>🔀 {tx('shuffle')}</button>
                <button className="an-ctrl-btn" onClick={removeLast} disabled={picked.length === 0}>⌫ {tx('undo')}</button>
                <button className="an-ctrl-btn" onClick={skip}>⏭ {tx('skip')}</button>
            </div>
        </div>
    );
}